import AboutMobPremiumBlock from "@/components/AboutMobPremiumBlock";
import DraggableScrollArea from "@/components/DraggableScrollArea";
import EntryGrid from "@/components/EntryGrid";
import Rail from "@/components/Rail";
import RailCarousel from "@/components/RailCarousel";
import ShoppingListContainer from "@/components/ShoppingList/ShoppingListContainer";
import SimilarEntries from "@/components/SimilarEntries";
import SplitViewLayout from "@/components/SplitViewLayout";
import TestimonialsSection from "@/components/TestimonialsSection";
import UsefulLinksListing from "@/components/UsefulLinksListing";
import { useAdContext } from "@/contexts/AdContext";
import { useHeaderContext } from "@/contexts/HeaderContext";
import useFaqs from "@/hooks/useFaqs";
import { createSearchHref } from "@/lib/algoliaUtils";
import getCommonStaticProps from "@/lib/getCommonStaticProps";
import AdRosTop from "@/src/ads/components/AdRosTop";
import createBreadcrumbsJsonLd from "@/src/recipes/lib/createBreadcrumbsJsonLd";
import FaqsSection from "@components/FaqsSection";
import * as Tabs from "@radix-ui/react-tabs";
import clsx from "classnames";
import craft from "lib/craft";
import Head from "next/head";
import QueryRecipeCollection from "queries/QueryRecipeCollection.graphql";
import QueryRecipeCollectionsIndex from "queries/QueryRecipeCollectionsIndex.graphql";
import QuerySlugs from "queries/QuerySlugs.graphql";
import { useEffect, useState } from "react";
import { Seomatic } from "react-seomatic";

export async function getStaticPaths() {
  const response = await craft([
    {
      document: QuerySlugs,
      variables: {
        section: "collections",
        limit: process.env.VERCEL_ENV !== "production" ? 1 : 1,
      },
    },
  ]);

  const { entries } = response[0] || {};

  const paths = entries.map((entry) => ({
    params: { slug: entry.slug },
  }));

  return { paths, fallback: "blocking" };
}

export async function getStaticProps({ params, previewData, preview }) {
  // Fetch common static properties
  const commonStaticProps = await getCommonStaticProps();

  const { slug } = params;

  const queries = [
    {
      document: QueryRecipeCollectionsIndex,
    },
    {
      document: QueryRecipeCollection,
      variables: {
        slug,
      },
    },
  ];

  const response = await craft(queries, preview ? previewData.token : null);

  const { pageEntry } = response[0];
  const { recipeCollection } = response[1];

  const breadcrumbs = [
    { label: "Recipes", href: "/recipes" },
    { label: "Collections", href: "/recipes/collections" },
    { label: recipeCollection.title, href: null },
  ];

  if (!recipeCollection) {
    return {
      props: {
        ...commonStaticProps,
      },
      notFound: true,
    };
  }

  const jsonLdSchema = [createBreadcrumbsJsonLd(breadcrumbs)];

  return {
    props: {
      ...commonStaticProps,
      pageEntry,
      seomatic: recipeCollection.seomatic,
      recipeCollection,
      breadcrumbs,
      jsonLdSchema,
    },
    revalidate: process.env.VERCEL_ENV !== "production" ? 1 : 3600,
  };
}

const CollectionEntry = ({
  recipeCollection,
  pageEntry,
  globalFaqs,
  premiumPromotion,
  testimonials,
}) => {
  const { recipes } = recipeCollection || {};

  // Set showAds to true for this page
  const { setShowAds } = useAdContext();
  useEffect(() => setShowAds(true));

  // Show the header border for this page
  const { setShowBorder } = useHeaderContext();
  useEffect(() => {
    setShowBorder(true);
    return () => setShowBorder(false);
  }, []);

  const heroImage = recipeCollection?.heroImage?.[0];

  const breadcrumbHref = createSearchHref({ section: "Collections" });

  // Filter recipes based on whether showAllEntries is true or not
  const filteredRecipes = recipes.filter((recipe) => {
    const { status } = recipe;
    return recipeCollection.showAllEntries ? true : status === "live";
  });

  const faqsToDisplay = useFaqs(
    globalFaqs.items,
    recipeCollection.collectionFaqs,
    "recipeCollections",
    false
  );

  /*
		Algolia
	*/

  // useSendConversionEventToAlgolia(recipeCollection); // TODO: no longer sending events to algolia; remove entirely eventually

  const currentDay = new Date().getDay();

  const testimonialsToDisplay = testimonials?.filter((testimonial) =>
    testimonial.showTestimonialOnPages.includes("recipeCollections")
  );

  // This is temporary whilst recipe collections are being populated with rails.
  // The conditionals in this file can be removed once populated is complete
  const collectionHasRails =
    recipeCollection.rails && recipeCollection.rails.length > 0;

  return (
    <>
      <Seomatic Head={Head} {...recipeCollection.seomatic} />
      <ShoppingListContainer />
      <div className={clsx("border-b")}>
        <SplitViewLayout
          heading={recipeCollection.title}
          introduction={recipeCollection.summary}
          images={[heroImage]}
          addVerticalPaddingToChildren={!collectionHasRails}
        />
      </div>

      <div>
        {collectionHasRails ? (
          <>
            <AdRosTop />
            <div className={clsx("pt-6")}>
              {recipeCollection.rails.map((rail, _i) => {
                return (
                  <>
                    {rail.typeHandle === "editorialRail" ? (
                      <RailCarousel
                        entries={rail.recipes}
                        key={rail.id}
                        summary={rail.summary}
                        heading={rail.frontendTitle ?? rail.title}
                        className="space-y-4 py-6 lg:space-y-6 lg:py-8 xl:py-12 2xl:space-y-8"
                        showSummary
                        railId={rail.id}
                      />
                    ) : (
                      <Rail
                        rail={rail}
                        shuffleSeed={currentDay}
                        key={rail.id}
                        showSummary
                      />
                    )}

                    {(_i === 1 ||
                      (_i === recipes.length - 1 && recipes.length === 1)) && (
                      <AboutMobPremiumBlock
                        key="about-mob-premium-block"
                        blockData={premiumPromotion}
                      />
                    )}
                  </>
                );
              })}
              <TestimonialsSection testimonials={testimonialsToDisplay} />
              <_RecipeCollectionTabs
                entryId={recipeCollection.id}
                usefulLinks={recipeCollection.usefulLinks}
              />
            </div>
          </>
        ) : (
          <>
            <AdRosTop className={clsx("!border-b-0 !pt-0")} />
            <div className={clsx("border-b px-6 py-6 lg:px-12 lg:py-12")}>
              <div className={clsx("CollectionEntry__recipeListWrapper")}>
                <div className={clsx("CollectionEntry__recipeList", "")}>
                  <EntryGrid entries={filteredRecipes} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const _RecipeCollectionTabs = ({ entryId, usefulLinks }) => {
  const tabList = [
    {
      value: "related-recipes",
      label: "Related Recipes",
    },
    { value: "useful-links", label: "Useful Links" },
  ];

  const [value, setValue] = useState(tabList[0].value);

  if (usefulLinks && usefulLinks.length > 0) {
    return (
      <div className={clsx("py-6 lg:py-10")}>
        <Tabs.Root defaultValue={value} onValueChange={setValue}>
          <div className="lg:px-12">
            <Tabs.List className={clsx("border-b py-5")}>
              <div className={clsx("relative")}>
                <DraggableScrollArea>
                  <div className={clsx("flex space-x-6 px-6 lg:px-3")}>
                    {tabList.map((tab) => {
                      const isActive = value === tab.value;

                      return (
                        <div key={tab.value} className={clsx("")}>
                          <Tabs.Trigger
                            value={tab.value}
                            className={clsx(
                              "whitespace-nowrap text-base transition ease-out",
                              {
                                "text-zinc-950": isActive,
                                "text-zinc-400 hover:text-zinc-900": !isActive,
                              }
                            )}
                          >
                            {tab.label}
                          </Tabs.Trigger>
                        </div>
                      );
                    })}
                  </div>
                </DraggableScrollArea>
              </div>
            </Tabs.List>
          </div>
          <div className={clsx("pt-6 lg:pt-8")}>
            <Tabs.Content value="related-recipes">
              <SimilarEntries entryId={entryId} />
            </Tabs.Content>
            <Tabs.Content value="useful-links">
              <div className="px-6 lg:px-12">
                <UsefulLinksListing groups={usefulLinks} />
              </div>
            </Tabs.Content>
          </div>
        </Tabs.Root>
      </div>
    );
  }

  return (
    <div className="py-6 lg:py-10">
      <SimilarEntries entryId={entryId} />
    </div>
  );
};

export default CollectionEntry;
